import React, { ReactElement, useContext } from 'react'
import { AppContext } from '../contexts/AppContext'
import { Link, useLocation } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { formatDayInterval, formatDayIntervalBetween, formatDate, formatTime, outputTime, dateOffset } from '../helpers/format'
import { FlipProp, IconProp } from '@fortawesome/fontawesome-svg-core'

type StatisticValue = string | number | boolean | undefined | null
type StatisticUnit = 'count' | 'interval' | 'frequency' | 'days_ago' | 'days_duration' | 'date' | 'percent' | 'time' | undefined
export type StatisticProps = {
    icon: IconProp
    /** Title and description of the statistic */
    name: string
    link?: string
    value?: StatisticValue
    units?: StatisticUnit
    prefix?: string
    suffix?: string
    flip?: FlipProp
}

const tidyValue = (value: StatisticValue, units: StatisticUnit): StatisticValue => {
    if (typeof value === 'number' && !Number.isInteger(value)) {
        value = +value.toFixed(2)
    }
    switch (units) {
        case 'time':
            return formatTime(value?.toString()) || undefined
        case 'days_ago':
        case 'date':
            return formatDate(dateishValue(value))
        default:
            return value
    }
}

const dateishValue = (value: StatisticValue): string | number | undefined =>
    typeof value === 'boolean' ? Number(value) : value || undefined
const numberValue = (value: StatisticValue): number =>
    typeof value !== 'number' ? 0 : value

const formatForText = (value: StatisticValue, units: StatisticUnit, suffix: string | undefined): string | undefined => {
    const formattedValue = formatValue(value, units)
    const text = typeof formattedValue === 'object' ? value : formattedValue
    return suffix ? `${text} ${suffix}` : String(text)
}

const formatForOutput = (value: StatisticValue, units: StatisticUnit, suffix: string | undefined): ReactElement | string | undefined => {
    return suffix
        ? <>{formatValue(value, units)} {suffix}</>
        : formatValue(value, units)
}

const formatValue = (value: StatisticValue, units: StatisticUnit): ReactElement | string | undefined => {
    if (typeof value === 'number' && !Number.isInteger(value)) {
        value = +value.toFixed(2)
    }
    switch (units) {
        case 'count':
            return '×' + value
        case 'interval':
            return formatDayInterval(numberValue(value))
        case 'frequency':
            return value + '/d'
        case 'days_ago':
            return formatDayIntervalBetween(dateishValue(value) || new Date())
        case 'days_duration':
            return '+' + dateOffset(typeof value !== 'string' ? value?.toString() : value)
        case 'date':
            return formatDate(dateishValue(value), 'short')
        case 'time':
            return outputTime(value ? value?.toString() : '')
        case 'percent':
            return (typeof value == 'number' ? Math.round(value * 100) : value) + '%'
        default:
            return typeof value === 'boolean'
                ? undefined
                : (Number.isNaN(value)
                    ? value?.toString()
                    : value?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','))
    }
}

const Statistic = (props: StatisticProps): ReactElement => {
    const { appDispatch } = useContext(AppContext)
    const location = useLocation().pathname
    if (!props.value) {
        return <></>
    }
    const linkValue = tidyValue(props.value, props.units)
    const value = formatForOutput(props.value, props.units, props.suffix)
    const valueAsText = formatForText(props.value, props.units, props.suffix)
    const linkTarget = props.link && linkValue ? props.link.toString().replace(':value', linkValue?.toString()) : null
    const output = props.link && linkTarget
        ? (location === linkTarget
            ? <span className="statistic-link-current">{value}</span>
            : <Link role="link" to={ linkTarget }>{value}</Link>)
        : value
    const message = `${valueAsText}${typeof props.name === 'string' ? ` — ${props.name}` : ''}`
    return (
        <span
            className="statistic"
            title={ props.name }
            onMouseOver={ () => appDispatch({ type: 'SET_NOTIFICATION_TEXT', message, icon: props.icon }) }
            onMouseOut={ () => appDispatch({ type: 'CLEAR_NOTIFICATION_TEXT', message }) }
            onClick={ () => appDispatch({ type: 'CLEAR_NOTIFICATION_TEXT', message }) }>
            { props.prefix && (
                linkTarget
                    ? <Link role="link" to={ linkTarget }>{props.prefix} </Link>
                    : props.prefix + ' ') }
            <FontAwesomeIcon icon={ props.icon } flip={ props.flip } /> {output}
        </span>
    )
}

export default Statistic
